import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAzureAuth } from '../../../hooks/useAzureAuth';
import { useAuthDispatch } from '../../../contexts/auth-context';
import { login } from '../../../contexts/actions/authActions';
import classes from './AuthCallback.module.scss';

// Types
interface AuthCallbackProps {
  redirectPath?: string;
}

interface AuthCallbackState {
  isProcessing: boolean;
  error: string | null;
  step: string;
}

// Module-level state management
let callbackState: AuthCallbackState = {
  isProcessing: true,
  error: null,
  step: 'Validating your credentials...'
};

// Module-level subscribers
let subscribers: ((state: AuthCallbackState) => void)[] = [];

// Function-based state management
const updateCallbackState = (newState: Partial<AuthCallbackState>): void => {
  callbackState = { ...callbackState, ...newState };
  notifySubscribers();
};

const resetCallbackState = (): void => {
  callbackState = {
    isProcessing: true,
    error: null,
    step: 'Validating your credentials...'
  };
  notifySubscribers();
};

const getCallbackState = (): AuthCallbackState => {
  return { ...callbackState };
};

const notifySubscribers = (): void => {
  subscribers.forEach(callback => callback(callbackState));
};

const subscribe = (
  callback: (state: AuthCallbackState) => void
): (() => void) => {
  subscribers.push(callback);
  return () => {
    subscribers = subscribers.filter(sub => sub !== callback);
  };
};

const processAuthenticationSteps = async (
  handleAuthCallback: () => Promise<any>,
  dispatch: any,
  redirectPath: string,
  history: any
): Promise<void> => {
  try {
    updateCallbackState({ isProcessing: true, error: null });

    // Step 1: Validate credentials
    updateCallbackState({ step: 'Validating your credentials...' });

    // Step 2: Process auth response
    updateCallbackState({ step: 'Processing authentication response...' });
    const authResult = await handleAuthCallback();

    if (authResult && authResult.accessToken) {
      // Step 3: Authenticate with server
      updateCallbackState({
        step: 'Authenticating with application server...'
      });

      // Call the existing login action with Azure token
      await login({ azureToken: authResult.accessToken })(dispatch);

      // Step 4: Success and redirect
      updateCallbackState({
        step: 'Authentication successful! Redirecting...'
      });

      // Handle redirect
      await handleSuccessfulAuth(redirectPath, history);
    } else {
      throw new Error('No access token received from authentication');
    }
  } catch (err) {
    handleAuthError(err, history);
  } finally {
    updateCallbackState({ isProcessing: false });
  }
};

const handleSuccessfulAuth = async (
  redirectPath: string,
  history: any
): Promise<void> => {
  // Get redirect path from session storage or use default
  const intendedPath =
    sessionStorage.getItem('auth_redirect_path') || redirectPath;
  sessionStorage.removeItem('auth_redirect_path');

  history.replace(intendedPath);
};

const handleAuthError = (err: any, history: any): void => {
  console.error('Auth callback processing failed:', err);
  const errorMessage =
    err instanceof Error ? err.message : 'Authentication failed';
  updateCallbackState({ error: errorMessage });

  // Redirect to login after a delay
  history.push('/login');
};

// Progress step utility functions
const isStepActive = (currentStep: string, stepToCheck: string): boolean => {
  return currentStep !== stepToCheck ? false : true;
};

// UI Component functions
const renderProcessingUI = (step: string) => (
  <div className={classes.callbackContainer}>
    <div className={classes.spinner}>
      <div className={classes.spinnerCircle}></div>
    </div>
    <p className={classes.message}>{step}</p>
    <div className={classes.progressSteps}>
      <div className={`${classes.progressStep} ${classes.active}`}></div>
      <div
        className={`${classes.progressStep} ${
          !isStepActive(step, 'Validating your credentials...')
            ? classes.active
            : ''
        }`}
      ></div>
      <div
        className={`${classes.progressStep} ${
          step === 'Authentication successful! Redirecting...'
            ? classes.active
            : ''
        }`}
      ></div>
    </div>
  </div>
);

const renderErrorUI = (error: string) => (
  <div className={classes.callbackContainer}>
    <div className={classes.errorIcon}>⚠️</div>
    <h2 className={classes.errorTitle}>Authentication Failed</h2>
    <p className={classes.errorMessage}>{error}</p>
    <p className={classes.redirectMessage}>Redirecting to login page...</p>
  </div>
);

const renderSuccessUI = () => (
  <div className={classes.callbackContainer}>
    <div className={classes.successIcon}>✓</div>
    <p className={classes.message}>Authentication successful! Redirecting...</p>
  </div>
);

// Main AuthCallback component using function-based approach
const AuthCallback: React.FC<AuthCallbackProps> = ({ redirectPath = '/' }) => {
  const history = useHistory();
  const { handleAuthCallback, authState } = useAzureAuth();
  const dispatch = useAuthDispatch();

  // Local state that syncs with module-level state
  const [localState, setLocalState] = useState<AuthCallbackState>(
    getCallbackState()
  );

  // Subscribe to module-level state changes
  useEffect(() => {
    const unsubscribe = subscribe(setLocalState);
    return unsubscribe;
  }, []);

  // Process authentication callback
  useEffect(() => {
    processAuthenticationSteps(
      handleAuthCallback,
      dispatch,
      redirectPath,
      history
    );
  }, [handleAuthCallback, dispatch, redirectPath, history]);

  // Render based on current state
  if (localState.isProcessing) {
    return renderProcessingUI(localState.step);
  }

  if (localState.error || authState.error) {
    return renderErrorUI(
      localState.error || authState.error || 'Authentication failed'
    );
  }

  return renderSuccessUI();
};

// Export individual functions for testing and reuse
export {
  updateCallbackState,
  resetCallbackState,
  getCallbackState,
  processAuthenticationSteps,
  handleSuccessfulAuth,
  handleAuthError,
  renderProcessingUI,
  renderErrorUI,
  renderSuccessUI
};

export default AuthCallback;
